import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import useStyles from "./style";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Slide,
  DialogActions,
  Typography,
  Button,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDropzone } from "react-dropzone";

import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";

//
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const UploadFiles = ({ openDialog, handleClose, onUploadDocument }) => {
  const classes = useStyles();
  const intl = useIntl();
  const t = intl.formatMessage;

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  const maxFileSize = 5242880;
  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  const fileLengthValidator = (file) => {
    if (file.size > maxFileSize) {
      alert(`File size is larger than ${formatBytes(maxFileSize)}`);
      return {
        code: "file-too-large",
        message: `File size is larger than ${formatBytes(maxFileSize)}`,
      };
    }
    return null;
  };

  // Dropzone props
  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxSize: maxFileSize,
    maxFiles: 1,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png"],
      "application/pdf": [".pdf"],
    },
    validator: fileLengthValidator,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        setSelectedFile(acceptedFiles[0]);
      }
    },
    onDropRejected: (rejectedFiles) => {
      const invalidFormats = rejectedFiles.map((file) => file.type).join(", ");
      console.error(`Invalid file format(s): ${invalidFormats}`);
    },
  });

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl("");
      return undefined;
    }

    const filePreviewUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(filePreviewUrl);

    return () => URL.revokeObjectURL(filePreviewUrl);
  }, [selectedFile]);

  useEffect(() => {
    if (!openDialog) {
      setSelectedFile(null);
    }
  }, [openDialog]);

  const isImagePreview =
    selectedFile?.type?.startsWith("image/") ||
    /\.(jpeg|jpg|png|gif|webp)$/i.test(selectedFile?.name || "");

  const handleUpload = () => {
    if (!selectedFile) {
      alert(t({ id: "driver.upload.chooseFirst" }));
      return;
    }

    if (typeof onUploadDocument !== "function") {
      alert(t({ id: "driver.upload.handlerUnavailable" }));
      return;
    }

    const uploadResult = onUploadDocument(selectedFile);
    if (uploadResult && typeof uploadResult.then === "function") {
      uploadResult.then(() => handleClose?.()).catch(() => {});
      return;
    }

    handleClose?.();
  };

  return (
    <>
      <Dialog
        open={openDialog}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        maxWidth={"md"}
        aria-describedby="dialog-box"
        className={classes.root}
      >
        <DialogTitle>
          <section className={classes.dialogTitleContainer}>
            <Typography variant="body1" className={classes.title}>
              {t({ id: "driver.upload.title" })}
            </Typography>
            <IconButton aria-label="close" onClick={handleClose}>
              <CloseIcon className={classes.closeIcon} />
            </IconButton>
          </section>
        </DialogTitle>
        <DialogContent>
          <div className={classes.uploadAreaWrapper}>
            <section
              {...getRootProps()}
              onClick={open}
              className={classes.fileUploadArea}
            >
              <input {...getInputProps()} />
              <div className={classes.fileUploadContentsStyle}>
                <div>
                  <div className={classes.fileIconWrapper}>
                    <InsertDriveFileOutlinedIcon
                      style={{ fontSize: "100px" }}
                    />
                    <div className={classes.uploadIconWrapper}>
                      <FileUploadOutlinedIcon />
                    </div>
                  </div>
                </div>
                <Typography variant="body1" style={{ marginTop: "10px" }}>
                  {t({ id: "driver.upload.dragDrop" })}{" "}
                  <span style={{ textDecoration: "underline" }}>
                    {t({ id: "driver.upload.chooseFile" })}
                  </span>
                </Typography>
              </div>
            </section>

            <div className={classes.formatInfoWrapper}>
              <Typography variant="body1" className={classes.supportInfo}>
                {t({ id: "driver.upload.formats" })}
              </Typography>
              <Typography variant="body1" className={classes.fileSizeInfo}>
                {t({ id: "driver.upload.maxSize" })}
              </Typography>
            </div>

            {selectedFile && previewUrl && (
              <div className={classes.selectedFilePreviewWrapper}>
                <Typography variant="body1" className={classes.previewTitle}>
                  {t({ id: "driver.upload.preview" })}
                </Typography>
                {isImagePreview ? (
                  <img
                    src={previewUrl}
                    alt={selectedFile.name}
                    className={classes.previewMedia}
                  />
                ) : (
                  <iframe
                    title={selectedFile.name}
                    src={previewUrl}
                    className={classes.previewMedia}
                  />
                )}
                <Typography variant="body2" className={classes.selectedFileName}>
                  {selectedFile.name}
                </Typography>
              </div>
            )}
          </div>
        </DialogContent>
        <Divider />
        <DialogActions>
          <section className={classes.dialogActions}>
            <section className={classes.closeBtn}>
              <Button onClick={handleClose} style={{ marginRight: "10px" }}>
                {t({ id: "driver.modal.close" })}
              </Button>
            </section>
            <section className={classes.closeBtn}>
              <Button onClick={handleUpload} disabled={!selectedFile}>
                {t({ id: "driver.docs.upload" })}
              </Button>
            </section>
          </section>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UploadFiles;
